import React, { useEffect, useRef } from "react";
import type { ICharacter } from "~/data/useCharacterData";
import Link from "next/link";
import ImageWithFallback from "~/components/ui/Image/Image";
import { ipfsHttpGateway, sanitizedImageURL } from "~/utils";
import { ChatBubbleLeftEllipsisIcon } from "@heroicons/react/24/outline";

export const InventoryItemSkeleton: React.FC = () => {
  return (
    <div className="group relative animate-pulse opacity-20">
      <div className="min-h-80 bg-dark-00 aspect-h-1 aspect-w-1 w-full overflow-hidden rounded-md">
        <div className="h-full rounded bg-dark-100" />
      </div>
    </div>
  );
};

export const InventoryItem: React.FC<
  Partial<ICharacter> & {
    isLast: boolean;
    hitCurrentLastItem?: () => void;
    actionsComponent?: (props: Partial<ICharacter>) => React.ReactNode;
  }
> = ({ ...props }) => {
  const {
    isLast,
    hitCurrentLastItem,
    actionsComponent,
    assistantId,
    modelPreview,
    assistantName,
    facingDescription,
    creator,
  } = props;
  // TODO: implement message count
  const messageCount = 0;

  const itemRef = useRef(null);

  useEffect(() => {
    if (!itemRef?.current || !hitCurrentLastItem) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (isLast && entry?.isIntersecting) {
        hitCurrentLastItem();
        observer.unobserve(entry.target);
      }
    });

    observer.observe(itemRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLast]);

  return (
    <button
      className="group relative cursor-pointer overflow-hidden rounded-lg border-2 border-gray-900 bg-dark-200 p-0 shadow-sm hover:animate-pulse hover:border-primary-100"
      data-id={assistantId}
      ref={itemRef}
    >
      <Link href={`/explore-npcs/${assistantId || ""}`}>
        <div className="absolute inset-0 z-10 cursor-pointer rounded-lg shadow-[inset_0_0_1.17589px_#fff_,_inset_0_0_14.1107px_hsla(0,0%,100%,.1)]" />
        <div className="min-w-80 aspect-h-1 aspect-w-1 w-full overflow-hidden">
          <ImageWithFallback
            src={sanitizedImageURL(
              modelPreview || "/images/placeholder-3d.jpeg"
            )}
            fallback={ipfsHttpGateway(
              modelPreview || "/images/placeholder-3d.jpeg"
            )}
            alt={modelPreview || "/images/placeholder-3d.jpeg"}
            className="h-full w-full object-cover object-center lg:h-full lg:w-full"
            width={400}
            height={400}
          />
        </div>
        <div className="h-full px-2 pb-8 pt-4">
          <div className="overflow-hidden">
            <h3 className="text-md truncate whitespace-nowrap break-all text-primary-100">
              {assistantName}
            </h3>
            <p className="mb-auto line-clamp-5 h-[76px] text-left text-xs text-gray-500">
              {facingDescription}
            </p>

            <div className="mb-auto mt-6 flex items-center gap-1 text-xs text-gray-500">
              <div className="inline-grid">
                {creator && (
                  <div className="truncate whitespace-nowrap break-all text-left">
                    @{creator}
                  </div>
                )}
              </div>
              <div className="inline-grid flex-grow">
                {(messageCount || messageCount === 0) && (
                  <div className="flex items-center justify-end gap-1">
                    <ChatBubbleLeftEllipsisIcon className="inline-block h-3 w-3" />
                    <span>{messageCount}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {actionsComponent && actionsComponent({ ...props })}
      </Link>
    </button>
  );
};
